import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { useCurrentUser } from "@/shared/auth/useAuth";
import { Role } from "@/shared/types/role";
import { CompliancePageShell } from "../shared/CompliancePageShell";
import { useDocuments } from "./use-documents";
import { CreateDocumentDialog } from "./CreateDocumentDialog";
import { StatusBadge } from "../shared/StatusBadge";
import { useTosRoleScopes } from "../shared/use-tos-role-scopes";
import { orderRoleScopes, roleScopeLabel } from "../shared/role-scopes";
import type { TermsOfService, TosRoleScope, TosState } from "../shared/compliance-types";
import { documentTypeLabel } from "../shared/document-type-label";

// Within a (roleScope, documentTypeId) lineage we want the active /
// soon-to-retire row first, then drafts on deck, then history. Keeps
// the most relevant row at the top of each group.
const STATE_ORDER: Record<TosState, number> = {
  ACTIVE: 0,
  SCHEDULED_RETIRE: 1,
  DRAFT: 2,
  RETIRED: 3,
};

export function DocumentsPage() {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const canEdit = currentUser?.role === Role.WANDERFIT_ADMIN;

  const [createOpen, setCreateOpen] = useState(false);
  const documentsQuery = useDocuments({});
  const roleScopes = useTosRoleScopes();

  // Section order = every scope the catalog reports, plus any scope a
  // document already carries that the catalog list hasn't caught up
  // with (stale cache, or the scopes request failed and we're on the
  // fallback). A document must never disappear because its scope is
  // unknown to the BO.
  const scopes = useMemo(
    () =>
      orderRoleScopes([
        ...roleScopes.scopes,
        ...(documentsQuery.data ?? []).map((doc) => doc.roleScope),
      ]),
    [roleScopes.scopes, documentsQuery.data],
  );

  const grouped = useMemo(() => {
    const out = new Map<TosRoleScope, TermsOfService[]>();
    scopes.forEach((scope) => out.set(scope, []));
    documentsQuery.data?.forEach((doc) => {
      out.get(doc.roleScope)?.push(doc);
    });
    out.forEach((list) => {
      list.sort((a, b) => {
        const typeCmp = documentTypeLabel(a).localeCompare(documentTypeLabel(b));
        if (typeCmp !== 0) return typeCmp;
        const stateCmp = STATE_ORDER[a.state] - STATE_ORDER[b.state];
        if (stateCmp !== 0) return stateCmp;
        return b.effectiveFrom.localeCompare(a.effectiveFrom);
      });
    });
    return out;
  }, [scopes, documentsQuery.data]);

  const headerActions = canEdit ? (
    <Button data-testid="compliance-new-document-button" onClick={() => setCreateOpen(true)}>
      <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
      {t("compliance.documents.create.button")}
    </Button>
  ) : null;

  return (
    <CompliancePageShell
      screenTestId="compliance-documents-screen"
      titleKey="compliance.documents.title"
      subtitleKey="compliance.documents.subtitle"
      headerActions={headerActions}
    >
      {documentsQuery.isLoading && (
        <p data-testid="compliance-documents-loading" className="text-sm text-muted-foreground">
          {t("compliance.documents.loading")}
        </p>
      )}

      {documentsQuery.isError && (
        <p data-testid="compliance-documents-error" className="text-sm text-destructive">
          {t("compliance.documents.loadError")}
        </p>
      )}

      {documentsQuery.data && documentsQuery.data.length === 0 && (
        <p data-testid="compliance-documents-empty" className="text-sm text-muted-foreground">
          {t("compliance.documents.empty")}
        </p>
      )}

      {documentsQuery.data && documentsQuery.data.length > 0 && (
        <div className="space-y-6">
          {scopes.map((scope) => {
            const docs = grouped.get(scope) ?? [];
            if (docs.length === 0) return null;
            return (
              <section
                key={scope}
                data-testid={`compliance-documents-group-${scope}`}
                className="space-y-2"
              >
                <h2 className="text-lg font-semibold">{roleScopeLabel(t, scope)}</h2>
                <ul className="space-y-2">
                  {docs.map((doc) => (
                    <DocumentRow key={doc.id} doc={doc} />
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}

      <CreateDocumentDialog open={createOpen} onOpenChange={setCreateOpen} />
    </CompliancePageShell>
  );
}

interface DocumentRowProps {
  readonly doc: TermsOfService;
}

function DocumentRow({ doc }: DocumentRowProps) {
  const { t } = useTranslation();
  return (
    <li
      data-testid={`compliance-document-row-${doc.id}`}
      className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-card p-4"
    >
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <p className="font-medium">{documentTypeLabel(doc)}</p>
          <StatusBadge state={doc.state} />
        </div>
        <p className="text-sm text-muted-foreground">
          v{doc.version} · {t("compliance.documents.effectiveFromLabel")}{" "}
          {new Date(doc.effectiveFrom).toLocaleString()}
          {doc.effectiveUntil != null && (
            <>
              {" · "}
              {t("compliance.documents.effectiveUntilLabel")}{" "}
              {new Date(doc.effectiveUntil).toLocaleString()}
            </>
          )}
        </p>
        {doc.locales && doc.locales.length > 0 && (
          <div
            className="flex flex-wrap items-center gap-1"
            data-testid={`compliance-document-row-locales-${doc.id}`}
          >
            {doc.locales.map((code) => (
              <code key={code} className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">
                {code}
              </code>
            ))}
          </div>
        )}
      </div>
      <Link
        to={`/compliance/documents/${doc.id}`}
        data-testid={`compliance-document-detail-link-${doc.id}`}
        className="text-sm font-medium text-wander-orange hover:underline"
      >
        {t("compliance.actions.view")} →
      </Link>
    </li>
  );
}
